import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Loader2, LogOut, Mail, User, Calendar, FileText, Sparkles, TrendingUp,
  Wallet, Send, ClipboardList, Star, RefreshCw, CheckCircle2, AlertTriangle,
  DollarSign, MessageCircle, Hash, XCircle, Clock, PlayCircle, Zap,
  Video, Upload, Youtube, Instagram, Facebook, Music2, Link2, ExternalLink,
  Radio, Users, PhoneCall, Ban, ThumbsUp, ThumbsDown, HandCoins, UserCheck,
  Bot, Bell, Save, CheckSquare, Timer, Pencil, ShieldCheck,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import {
  staffMe, staffSubmitLog, staffListLogs, staffGetScores,
  staffSubmitDepositClaim, staffListDepositClaims, staffEditDepositClaim,
  staffAttendanceToday, staffStartShift,
  staffSubmitVideoUpload, staffListVideoUploads,
  staffSubmitSignalDelivery, staffListSignalDeliveries, staffPatchSignalDelivery,
  staffListSignalCommissions, staffApproveSignalCommission,
  staffListSignalProviders,
  staffListPersonalCallRequests, staffClaimPersonalCall, staffCompletePersonalCall,
  staffUpdateWhatsapp, staffListReminders, staffAckReminder,
  setStaffToken, apiErrMsg, getStaffToken,
} from '../../lib/api';

const ROLE_META = {
  telegram_support: {
    label: 'Telegram Support',
    metric: 'notes_only',
    metricLabel: 'Optional daily note (no target)',
    placeholder: 'Optional summary…',
    accent: 'text-[hsl(199_84%_68%)]',
    unit: 'notes',
    hideMetric: true,
  },
  video_uploader: {
    label: 'Video Uploader',
    metric: 'video_count',
    metricLabel: 'Videos uploaded today',
    placeholder: 'e.g. 18',
    accent: 'text-[hsl(320_70%_78%)]',
    unit: 'videos',
  },
  market_analyst: {
    label: 'Market Analyst',
    metric: 'signal_count',
    metricLabel: 'Signals given today',
    placeholder: 'e.g. 3',
    accent: 'text-[hsl(43_92%_72%)]',
    unit: 'signals',
  },
};

function fmtBdt(v) { return `৳${Math.round(v || 0).toLocaleString('en-IN')}`; }
function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short' }); }
  catch { return iso; }
}

function scoreColor(score) {
  const s = Number(score || 0);
  if (s >= 90) return 'text-[hsl(140_65%_70%)]';
  if (s >= 75) return 'text-[hsl(199_84%_70%)]';
  if (s >= 60) return 'text-[hsl(43_92%_72%)]';
  return 'text-[hsl(0_72%_70%)]';
}
function adjustmentColor(pct) {
  const p = Number(pct || 0);
  if (p > 0) return 'text-[hsl(140_65%_70%)]';
  if (p < 0) return 'text-[hsl(0_72%_70%)]';
  return 'text-muted-foreground';
}

export default function StaffDashboardPage() {
  const nav = useNavigate();
  const [staff, setStaff] = useState(null);
  const [logs, setLogs] = useState([]);
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metricValue, setMetricValue] = useState('');
  const [winRate, setWinRate] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Deposit claims (Telegram support only)
  const [claims, setClaims] = useState([]);
  const [claimSummary, setClaimSummary] = useState(null);
  const [dcTelegramId, setDcTelegramId] = useState('');
  const [dcTraderId, setDcTraderId] = useState('');
  const [dcAmount, setDcAmount] = useState('');
  const [dcNote, setDcNote] = useState('');
  const [dcSubmitting, setDcSubmitting] = useState(false);
  // Attendance / shift start
  const [attendance, setAttendance] = useState(null);
  const [attStarting, setAttStarting] = useState(false);
  const [nowTick, setNowTick] = useState(Date.now());
  // Video uploader
  const [videoData, setVideoData] = useState(null); // { items, today, configured_channels }
  const [vuSlot, setVuSlot] = useState('10am');
  const [vuTiktok, setVuTiktok] = useState('');
  const [vuFacebook, setVuFacebook] = useState('');
  const [vuInstagram, setVuInstagram] = useState('');
  const [vuYoutube, setVuYoutube] = useState('');
  const [vuNote, setVuNote] = useState('');
  const [vuSubmitting, setVuSubmitting] = useState(false);
  // Signal Provider (market_analyst)
  const [signalData, setSignalData] = useState(null); // { items, today }
  const [commissionData, setCommissionData] = useState(null); // { items, monthly_summary_usd, pending_approval_for_me }
  const [signalProviders, setSignalProviders] = useState([]); // for MS dropdown
  const [sigStatus, setSigStatus] = useState('delivered'); // 'delivered' | 'inactive'
  const [sigTrader, setSigTrader] = useState('');
  const [sigTelegram, setSigTelegram] = useState('');
  const [sigInactiveReason, setSigInactiveReason] = useState('');
  const [sigLiveConverted, setSigLiveConverted] = useState(false);
  const [sigNote, setSigNote] = useState('');
  const [sigSubmitting, setSigSubmitting] = useState(false);
  const [approvingId, setApprovingId] = useState(null);
  // Telegram support: linked signal provider dropdown
  const [dcLinkedSpId, setDcLinkedSpId] = useState('');
  const [editingClaim, setEditingClaim] = useState(null); // for edit dialog
  // AI Supervisor (WhatsApp reminders + brief)
  const [reminderData, setReminderData] = useState(null); // { items, today: { brief, ack_count, ... } }
  const [waNumber, setWaNumber] = useState('');
  const [waSaving, setWaSaving] = useState(false);
  const [ackingId, setAckingId] = useState(null);
  // Personal Call Queue (market_analyst + telegram_support)
  const [pcallData, setPcallData] = useState(null); // { items, approved_open, called_open, min_deposit_usd }
  const [pcallFilter, setPcallFilter] = useState('open'); // 'open' | 'approved' | 'called' | 'completed' | 'all'
  const [pcallActionId, setPcallActionId] = useState(null);

  const roleMeta = useMemo(
    () => (staff ? ROLE_META[staff.role] || ROLE_META.telegram_support : null),
    [staff]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [me, ll, sc] = await Promise.all([staffMe(), staffListLogs(), staffGetScores()]);
      setStaff(me);
      setWaNumber(me.whatsapp_number || '');
      setLogs(ll.items || []);
      setScores(sc.items || []);
      // AI Supervisor reminders (all roles)
      try {
        const rr = await staffListReminders();
        setReminderData(rr);
      } catch { /* soft-fail */ }
      // If Telegram Support, also load deposit claims.
      if (me.role === 'telegram_support') {
        try {
          const cc = await staffListDepositClaims();
          setClaims(cc.items || []);
          setClaimSummary(cc.today || null);
        } catch { /* soft-fail */ }
      }
      // If Video Uploader, load daily uploads + configured channels.
      if (me.role === 'video_uploader') {
        try {
          const vv = await staffListVideoUploads();
          setVideoData(vv);
        } catch { /* soft-fail */ }
      }
      // If Market Analyst, load signals + commissions.
      if (me.role === 'market_analyst') {
        try {
          const [sd, sc] = await Promise.all([
            staffListSignalDeliveries(),
            staffListSignalCommissions(),
          ]);
          setSignalData(sd);
          setCommissionData(sc);
        } catch { /* soft-fail */ }
      }
      // If Telegram Support, also load the active signal-provider list + own commissions (for the linked dropdown).
      if (me.role === 'telegram_support') {
        try {
          const [sp, sc] = await Promise.all([
            staffListSignalProviders(),
            staffListSignalCommissions(),
          ]);
          setSignalProviders(sp.items || []);
          setCommissionData(sc);
        } catch { /* soft-fail */ }
      }
      // Personal Call Queue (visible to market_analyst + telegram_support)
      if (me.role === 'market_analyst' || me.role === 'telegram_support') {
        try {
          const pc = await staffListPersonalCallRequests();
          setPcallData(pc);
        } catch { /* soft-fail */ }
      }
      // Load attendance status for today
      try {
        const at = await staffAttendanceToday();
        setAttendance(at);
      } catch { /* soft-fail */ }
    } catch (e) {
      const msg = apiErrMsg(e, 'Failed to load');
      if ((e?.response?.status || 0) === 401) {
        setStaffToken(null);
        nav('/staff/login');
        return;
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [nav]);

  useEffect(() => {
    if (!getStaffToken()) {
      nav('/staff/login');
      return;
    }
    load();
  }, [load, nav]);

  // Live clock ticker (once per minute) — shows how much late a not-yet-started staff is right now.
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  async function startShift() {
    setAttStarting(true);
    try {
      const r = await staffStartShift();
      if (r.on_time) {
        toast.success('Shift started on time — great job! ✓');
      } else if (r.late_minutes < 60) {
        toast.warning(`Started ${Math.round(r.late_minutes)} min late — ৳${r.late_deduction_bdt} deducted`);
      } else {
        toast.error(`Started ${Math.round(r.late_hours * 10) / 10}h late — ৳${r.late_deduction_bdt} deducted from today's salary`);
      }
      await load();
    } catch (e) { toast.error(apiErrMsg(e, 'Failed to start shift')); }
    finally { setAttStarting(false); }
  }

  function logout() {
    setStaffToken(null);
    toast.success('Logged out');
    nav('/staff/login');
  }

  async function submitDepositClaim(e) {
    e.preventDefault();
    const amt = Number(dcAmount);
    if (!dcTelegramId.trim() || !dcTraderId.trim() || !Number.isFinite(amt) || amt <= 0) {
      toast.error('Fill in Telegram ID, Trader ID, and a valid amount');
      return;
    }
    setDcSubmitting(true);
    try {
      const r = await staffSubmitDepositClaim({
        telegram_id: dcTelegramId.trim(),
        quotex_trader_id: dcTraderId.trim(),
        claimed_amount_usd: amt,
        note: dcNote.trim(),
        linked_signal_provider_staff_id: dcLinkedSpId || null,
      });
      if (r.auto_verified) {
        toast.success('Claim auto-verified from recent Quotex postback! ✓');
      } else {
        toast.success('Claim submitted. Will auto-verify when Quotex postback arrives.');
      }
      setDcTelegramId('');
      setDcTraderId('');
      setDcAmount('');
      setDcNote('');
      setDcLinkedSpId('');
      await load();
    } catch (e2) {
      toast.error(apiErrMsg(e2, 'Submit failed'));
    } finally { setDcSubmitting(false); }
  }

  async function submitLog(e) {
    e.preventDefault();
    if (!staff || !roleMeta) return;
    const metrics = {};
    // Roles that need a numeric metric input (video_uploader, market_analyst)
    if (!roleMeta.hideMetric) {
      const num = Number(metricValue);
      if (!Number.isFinite(num) || num < 0) {
        toast.error(`Please enter a valid ${roleMeta.metricLabel}`);
        return;
      }
      metrics[roleMeta.metric] = num;
    }
    if (staff.role === 'market_analyst' && winRate !== '') {
      metrics.win_rate_pct = Math.max(0, Math.min(100, Number(winRate) || 0));
    }
    if (roleMeta.hideMetric && !note.trim()) {
      toast.error('Please write a short note about your day');
      return;
    }
    setSubmitting(true);
    try {
      await staffSubmitLog({ metrics, note });
      toast.success('Log submitted — AI is analyzing…');
      setMetricValue('');
      setWinRate('');
      setNote('');
      await load();
    } catch (e2) {
      toast.error(apiErrMsg(e2, 'Submit failed'));
    } finally {
      setSubmitting(false);
    }
  }

  async function submitVideoUpload(e) {
    e.preventDefault();
    if (!staff || staff.role !== 'video_uploader') return;
    const tiktok = vuTiktok.trim();
    if (!tiktok || !/tiktok\.com/i.test(tiktok)) {
      toast.error('Enter a valid TikTok URL');
      return;
    }
    const provided = [vuFacebook.trim(), vuInstagram.trim(), vuYoutube.trim()].filter(Boolean);
    if (provided.length < 2) {
      toast.error('Minimum 2 cross-post URLs required (FB / IG / YT)');
      return;
    }
    setVuSubmitting(true);
    try {
      await staffSubmitVideoUpload({
        slot: vuSlot,
        tiktok_url: tiktok,
        facebook_url: vuFacebook.trim(),
        instagram_url: vuInstagram.trim(),
        youtube_url: vuYoutube.trim(),
        note: vuNote.trim(),
      });
      toast.success(`Upload logged for ${vuSlot === 'flex' ? 'flex slot' : vuSlot.toUpperCase()} ✓`);
      setVuTiktok('');
      setVuFacebook('');
      setVuInstagram('');
      setVuYoutube('');
      setVuNote('');
      await load();
    } catch (e2) {
      toast.error(apiErrMsg(e2, 'Video submit failed'));
    } finally {
      setVuSubmitting(false);
    }
  }

  async function submitSignalDelivery(e) {
    e.preventDefault();
    if (!staff || staff.role !== 'market_analyst') return;
    if (sigStatus === 'delivered' && !sigTrader.trim()) {
      toast.error('Trader ID required for delivered signals');
      return;
    }
    if (sigStatus === 'inactive' && !sigInactiveReason.trim()) {
      toast.error('Reason required when marking inactive');
      return;
    }
    if (!sigTelegram.trim()) {
      toast.error('Telegram username required');
      return;
    }
    setSigSubmitting(true);
    try {
      await staffSubmitSignalDelivery({
        trader_id: sigStatus === 'delivered' ? sigTrader.trim() : '',
        telegram_username: sigTelegram.trim(),
        status: sigStatus,
        inactive_reason: sigStatus === 'inactive' ? sigInactiveReason.trim() : '',
        live_call_deposit_converted: sigStatus === 'delivered' && sigLiveConverted,
        note: sigNote.trim(),
      });
      if (sigStatus === 'delivered' && sigLiveConverted) {
        toast.success('Signal logged. Commission will be pending your approval once Quotex postback confirms the deposit.');
      } else {
        toast.success('Signal delivery logged ✓');
      }
      setSigTrader('');
      setSigTelegram('');
      setSigInactiveReason('');
      setSigNote('');
      setSigLiveConverted(false);
      await load();
    } catch (e2) {
      toast.error(apiErrMsg(e2, 'Signal submit failed'));
    } finally {
      setSigSubmitting(false);
    }
  }

  async function approveCommission(commissionId, approve) {
    setApprovingId(commissionId);
    try {
      await staffApproveSignalCommission(commissionId, approve);
      toast.success(approve ? 'Commission approved ✓' : 'Commission rejected');
      await load();
    } catch (e2) {
      toast.error(apiErrMsg(e2, 'Approval failed'));
    } finally {
      setApprovingId(null);
    }
  }

  async function toggleLiveConversion(deliveryId, current) {
    try {
      await staffPatchSignalDelivery(deliveryId, { live_call_deposit_converted: !current });
      toast.success(!current ? 'Marked as live-converted' : 'Unmarked live conversion');
      await load();
    } catch (e2) {
      toast.error(apiErrMsg(e2, 'Update failed'));
    }
  }

  async function saveWhatsapp() {
    const w = (waNumber || '').trim();
    if (w && !/^\+?[0-9\s\-()]{5,25}$/.test(w)) {
      toast.error('Invalid WhatsApp number');
      return;
    }
    setWaSaving(true);
    try {
      const updated = await staffUpdateWhatsapp(w);
      toast.success(w ? 'WhatsApp number saved ✓' : 'WhatsApp number cleared');
      setStaff((prev) => (prev ? { ...prev, whatsapp_number: updated.whatsapp_number } : prev));
    } catch (e2) {
      toast.error(apiErrMsg(e2, 'Save failed'));
    } finally {
      setWaSaving(false);
    }
  }

  async function ackReminder(reminderId, action) {
    setAckingId(reminderId);
    try {
      await staffAckReminder(reminderId, action);
      const label = action === 'done' ? 'done' : (action === 'skip' ? 'skipped' : 'snoozed');
      toast.success(`Reminder marked ${label} ✓`);
      const rr = await staffListReminders();
      setReminderData(rr);
    } catch (e2) {
      toast.error(apiErrMsg(e2, 'Ack failed'));
    } finally {
      setAckingId(null);
    }
  }

  async function claimPersonalCall(reqId) {
    setPcallActionId(reqId);
    try {
      await staffClaimPersonalCall(reqId, '');
      toast.success('Claimed. DM the user on Telegram now.');
      const pc = await staffListPersonalCallRequests();
      setPcallData(pc);
    } catch (e2) {
      toast.error(apiErrMsg(e2, 'Claim failed'));
    } finally {
      setPcallActionId(null);
    }
  }

  async function completePersonalCall(reqId, noteText = '') {
    setPcallActionId(reqId);
    try {
      await staffCompletePersonalCall(reqId, noteText);
      toast.success('Call marked completed ✓');
      const pc = await staffListPersonalCallRequests();
      setPcallData(pc);
    } catch (e2) {
      toast.error(apiErrMsg(e2, 'Complete failed'));
    } finally {
      setPcallActionId(null);
    }
  }

  const currentMonthKey = new Date().toISOString().slice(0, 7);
  const currentMonthScore = scores.find((s) => s.month === currentMonthKey);
  const todayIso = new Date().toISOString().slice(0, 10);
  const todaysLog = logs.find((l) => l.date === todayIso);

  return (
    <main
      className="min-h-screen bg-[hsl(0_0%_4%)] text-white pb-14"
      data-testid="staff-dashboard-page"
    >
      {/* Header */}
      <header className="sticky top-0 z-30 bg-[rgba(10,10,14,0.85)] backdrop-blur border-b border-white/8">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-md bg-gradient-to-br from-[hsl(43_92%_56%)] to-[hsl(38_85%_50%)] flex items-center justify-center">
              <User className="h-4 w-4 text-black" />
            </div>
            <div>
              <div className="font-display text-sm uppercase tracking-[0.18em]">AlveeFx Staff</div>
              <div className="text-[10px] text-muted-foreground">Performance & Salary Portal</div>
            </div>
          </div>
          <Button
            size="sm" variant="outline"
            className="h-8 border-white/10 bg-white/[0.02] gap-1.5"
            onClick={logout}
            data-testid="staff-logout"
          >
            <LogOut className="h-3.5 w-3.5" /> Log out
          </Button>
        </div>
      </header>

      {loading || !staff ? (
        <div className="p-16 text-center text-muted-foreground text-sm">
          <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> Loading…
        </div>
      ) : (
        <div className="max-w-5xl mx-auto px-4 pt-6 space-y-5">
          {/* Attendance / Start Shift */}
          <AttendanceCard
            att={attendance}
            starting={attStarting}
            onStart={startShift}
            nowTick={nowTick}
          />

          {/* Profile card */}
          <Card className="bg-white/[0.02] border-white/10">
            <CardContent className="p-4 sm:p-5 grid gap-4 md:grid-cols-[1fr,auto] items-start">
              <div className="min-w-0 space-y-1.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-display text-xl truncate">{staff.name}</div>
                  <Badge variant="outline" className={`text-[10px] uppercase tracking-[0.18em] border-white/15 ${roleMeta?.accent || ''}`}>
                    {staff.role_label}
                  </Badge>
                  {!staff.active && (
                    <Badge variant="outline" className="text-[10px] uppercase tracking-[0.18em] border-[rgba(225,29,46,0.55)] text-[hsl(0_72%_70%)]">
                      Deactivated
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5" /> {staff.email}
                </div>
                {staff.daily_targets && (
                  <div className="text-[11px] text-muted-foreground">
                    Daily target: <span className="text-white font-medium">
                      {Object.entries(staff.daily_targets).map(([k, v]) => `${v} ${k.replace('_', ' ')}`).join(' · ')}
                    </span>
                  </div>
                )}
              </div>
              <div className="rounded-lg border border-[hsl(43_92%_56%)]/40 bg-[rgba(245,185,66,0.05)] p-3 min-w-[220px] text-right">
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Base Salary</div>
                <div className="font-display text-2xl text-[hsl(43_92%_56%)] tabular-nums">{fmtBdt(staff.base_salary_bdt)}</div>
                {currentMonthScore ? (
                  <>
                    <div className="mt-1.5 pt-1.5 border-t border-white/8 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                      This month ({currentMonthKey})
                    </div>
                    <div className="mt-0.5 flex items-baseline justify-end gap-1.5">
                      <span className={`font-display text-lg tabular-nums ${scoreColor(currentMonthScore.total_score)}`}>
                        {currentMonthScore.total_score}
                      </span>
                      <span className="text-xs text-muted-foreground">/ 100</span>
                    </div>
                    <div className={`text-xs font-semibold tabular-nums ${adjustmentColor(currentMonthScore.adjustment_pct)}`}>
                      {currentMonthScore.adjustment_pct > 0 ? '+' : ''}{currentMonthScore.adjustment_pct}% → {fmtBdt(currentMonthScore.final_salary_bdt)}
                    </div>
                  </>
                ) : (
                  <div className="mt-1.5 text-[10px] text-muted-foreground">
                    Score compute pending — admin recompute korle jabe.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Deposit Claim (Telegram Support only) */}
          {staff.role === 'telegram_support' && (
            <Card className="bg-white/[0.02] border-white/10" data-testid="deposit-claim-card">
              <CardContent className="p-4 sm:p-5 space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <DollarSign className="h-4 w-4 text-[hsl(140_65%_70%)]" />
                  <div className="font-display text-sm">Customer Deposit Claim</div>
                  <Badge variant="outline" className="text-[9px] uppercase tracking-[0.18em] border-white/15 text-muted-foreground">
                    Auto-verified via Quotex postback
                  </Badge>
                </div>

                {/* Today progress */}
                {claimSummary && (
                  <div className="rounded-lg border border-[rgba(70,200,120,0.35)] bg-[rgba(70,200,120,0.05)] p-3">
                    <div className="flex items-baseline justify-between flex-wrap gap-2">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Today verified</div>
                        <div className="font-display text-2xl text-[hsl(140_65%_70%)] tabular-nums">
                          ${claimSummary.verified_usd.toFixed(0)}
                          <span className="text-sm text-muted-foreground"> / ${claimSummary.target_usd.toFixed(0)} target</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Pending</div>
                        <div className="font-display text-base text-[hsl(43_92%_72%)] tabular-nums">
                          ${claimSummary.pending_usd.toFixed(0)}
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 h-2 rounded-full bg-white/8 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[hsl(140_65%_50%)] to-[hsl(140_65%_65%)]"
                        style={{ width: `${Math.min(100, claimSummary.progress_pct)}%` }}
                      />
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1 tabular-nums">
                      {claimSummary.progress_pct.toFixed(1)}% of daily target
                    </div>
                  </div>
                )}

                <form onSubmit={submitDepositClaim} className="grid gap-3 sm:grid-cols-3">
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-1">
                      <MessageCircle className="h-3 w-3" /> Telegram ID
                    </Label>
                    <Input
                      value={dcTelegramId}
                      onChange={(e) => setDcTelegramId(e.target.value)}
                      placeholder="@username or +8801..."
                      className="bg-white/[0.03] border-white/10 h-10"
                      data-testid="deposit-claim-telegram"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-1">
                      <Hash className="h-3 w-3" /> Quotex Trader ID
                    </Label>
                    <Input
                      value={dcTraderId}
                      onChange={(e) => setDcTraderId(e.target.value)}
                      placeholder="e.g. 12345678"
                      className="bg-white/[0.03] border-white/10 h-10 font-mono"
                      data-testid="deposit-claim-trader"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-1">
                      <DollarSign className="h-3 w-3" /> Deposit Amount (USD)
                    </Label>
                    <Input
                      type="number" min="1" step="1"
                      value={dcAmount}
                      onChange={(e) => setDcAmount(e.target.value)}
                      placeholder="e.g. 500"
                      className="bg-white/[0.03] border-white/10 h-10 font-mono tabular-nums"
                      data-testid="deposit-claim-amount"
                    />
                  </div>
                  <div className="sm:col-span-3">
                    <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Note (optional)</Label>
                    <Input
                      value={dcNote}
                      onChange={(e) => setDcNote(e.target.value)}
                      placeholder="Anything you want to remember about this customer"
                      className="bg-white/[0.03] border-white/10 h-9 text-xs"
                      data-testid="deposit-claim-note"
                    />
                  </div>
                  {signalProviders.length > 0 && (
                    <div className="sm:col-span-3">
                      <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-1">
                        <Radio className="h-3 w-3 text-[hsl(280_70%_75%)]" /> Signal Provider (optional — for split commission)
                      </Label>
                      <select
                        value={dcLinkedSpId}
                        onChange={(e) => setDcLinkedSpId(e.target.value)}
                        className="w-full h-9 rounded-md bg-white/[0.03] border border-white/10 px-2 text-xs font-mono focus:outline-none focus:border-white/25"
                        data-testid="deposit-claim-linked-sp"
                      >
                        <option value="">— None (no signal provider attribution) —</option>
                        {signalProviders.map((sp) => (
                          <option key={sp.id} value={sp.id}>{sp.name} · {sp.email}</option>
                        ))}
                      </select>
                      <div className="text-[10px] text-muted-foreground mt-1">
                        Link if this deposit came from a signal provider&apos;s Telegram call. On postback verify, both get 0.5%/0.5%. Signal provider must approve.
                      </div>
                    </div>
                  )}
                  <div className="sm:col-span-3">
                    <Button
                      type="submit" disabled={dcSubmitting}
                      className="w-full h-11 bg-gradient-to-r from-[hsl(140_65%_50%)] to-[hsl(155_60%_45%)] hover:from-[hsl(140_65%_58%)] hover:to-[hsl(155_60%_52%)] text-white font-bold border-0 gap-2"
                      data-testid="deposit-claim-submit"
                    >
                      {dcSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                      Submit deposit claim
                    </Button>
                    <p className="text-[10px] text-muted-foreground mt-1.5 text-center">
                      Same Quotex Trader ID cannot be submitted twice within 24 hours (by you or any other staff).
                    </p>
                  </div>
                </form>

                {/* Claims list */}
                {claims.length > 0 && (
                  <div className="space-y-1.5 pt-2 border-t border-white/8">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                      Recent claims ({claims.length})
                    </div>
                    <div className="grid gap-1.5">
                      {claims.slice(0, 20).map((c) => (
                        <ClaimRow key={c.id} claim={c} onEdit={() => setEditingClaim(c)} />
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* AI Supervisor: WhatsApp number + today's reminders (all roles) */}
          <AiSupervisorCard
            reminderData={reminderData}
            waNumber={waNumber} setWaNumber={setWaNumber}
            saving={waSaving}
            onSaveWhatsapp={saveWhatsapp}
            onAck={ackReminder}
            ackingId={ackingId}
          />

          {/* Video Uploader (only for video_uploader role) */}
          {staff.role === 'video_uploader' && (
            <VideoUploaderCard
              data={videoData}
              slot={vuSlot} setSlot={setVuSlot}
              tiktok={vuTiktok} setTiktok={setVuTiktok}
              facebook={vuFacebook} setFacebook={setVuFacebook}
              instagram={vuInstagram} setInstagram={setVuInstagram}
              youtube={vuYoutube} setYoutube={setVuYoutube}
              note={vuNote} setNote={setVuNote}
              submitting={vuSubmitting}
              onSubmit={submitVideoUpload}
            />
          )}

          {/* Market Analyst / Signal Provider (only for market_analyst role) */}
          {staff.role === 'market_analyst' && (
            <MarketAnalystCard
              signalData={signalData}
              commissionData={commissionData}
              status={sigStatus} setStatus={setSigStatus}
              trader={sigTrader} setTrader={setSigTrader}
              telegram={sigTelegram} setTelegram={setSigTelegram}
              inactiveReason={sigInactiveReason} setInactiveReason={setSigInactiveReason}
              liveConverted={sigLiveConverted} setLiveConverted={setSigLiveConverted}
              note={sigNote} setNote={setSigNote}
              submitting={sigSubmitting}
              onSubmit={submitSignalDelivery}
              onApprove={approveCommission}
              onToggleLive={toggleLiveConversion}
              approvingId={approvingId}
            />
          )}

          {/* Personal Call Queue (market_analyst + telegram_support) */}
          {(staff.role === 'market_analyst' || staff.role === 'telegram_support') && (
            <PersonalCallQueueCard
              pcallData={pcallData}
              filter={pcallFilter}
              setFilter={setPcallFilter}
              onClaim={claimPersonalCall}
              onComplete={completePersonalCall}
              actionId={pcallActionId}
              currentStaffId={staff.id}
              onRefresh={load}
            />
          )}

          {/* Log submit + today status */}
          <Card className="bg-white/[0.02] border-white/10">
            <CardContent className="p-4 sm:p-5 space-y-3">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-[hsl(43_92%_56%)]" />
                <div className="font-display text-sm">Today&apos;s Work Log ({fmtDate(todayIso)})</div>
                {todaysLog && (
                  <Badge variant="outline" className="text-[10px] uppercase tracking-[0.18em] border-[rgba(70,200,120,0.5)] text-[hsl(140_60%_70%)]">
                    Submitted · will overwrite
                  </Badge>
                )}
              </div>
              <form onSubmit={submitLog} className="grid gap-3 sm:grid-cols-2">
                {!roleMeta.hideMetric && (
                  <div className="sm:col-span-1">
                    <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                      {roleMeta.metricLabel}
                    </Label>
                    <Input
                      type="number" min="0" step="1" required
                      value={metricValue}
                      onChange={(e) => setMetricValue(e.target.value)}
                      placeholder={roleMeta.placeholder}
                      className="bg-white/[0.03] border-white/10 font-mono tabular-nums h-10"
                      data-testid="staff-log-metric"
                    />
                  </div>
                )}
                {staff.role === 'market_analyst' && (
                  <div className="sm:col-span-1">
                    <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                      Win-rate today (%)
                    </Label>
                    <Input
                      type="number" min="0" max="100" step="0.1"
                      value={winRate}
                      onChange={(e) => setWinRate(e.target.value)}
                      placeholder="e.g. 75"
                      className="bg-white/[0.03] border-white/10 font-mono tabular-nums h-10"
                      data-testid="staff-log-winrate"
                    />
                  </div>
                )}
                <div className="sm:col-span-2">
                  <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                    Notes (Bangla ok — describe kajta)
                  </Label>
                  <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={3}
                    placeholder="e.g. Ajke 45 ta reply diyechi, 3 ta refund case handle korlam..."
                    className="bg-white/[0.03] border-white/10"
                    data-testid="staff-log-note"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-11 bg-gradient-to-r from-[hsl(43_92%_56%)] to-[hsl(38_85%_50%)] hover:from-[hsl(43_92%_62%)] hover:to-[hsl(38_85%_56%)] text-black font-bold border-0 gap-2"
                    data-testid="staff-log-submit"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    {todaysLog ? "Update today's log" : "Submit today's log"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Logs history */}
          <Card className="bg-white/[0.02] border-white/10">
            <CardContent className="p-4 sm:p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ClipboardList className="h-4 w-4 text-[hsl(199_84%_68%)]" />
                  <div className="font-display text-sm">Recent Logs ({logs.length})</div>
                </div>
                <Button size="sm" variant="outline" onClick={load} className="h-7 gap-1.5 border-white/10 bg-white/[0.02]">
                  <RefreshCw className="h-3 w-3" /> Refresh
                </Button>
              </div>
              {logs.length === 0 ? (
                <div className="text-sm text-muted-foreground text-center p-4 border border-white/10 rounded-md bg-white/[0.02]">
                  No logs yet — submit your first daily log above.
                </div>
              ) : (
                <div className="grid gap-2">
                  {logs.slice(0, 15).map((l) => (
                    <div
                      key={l.id}
                      className="rounded-md border border-white/10 bg-white/[0.02] p-3"
                      data-testid={`staff-log-row-${l.id}`}
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                          <Calendar className="h-3 w-3" /> {l.date}
                        </div>
                        <span className="text-[11px] font-mono text-white">
                          {Object.entries(l.metrics || {}).map(([k, v]) => `${v} ${k.replace('_', ' ')}`).join(' · ')}
                        </span>
                        {l.ai_status === 'done' && l.ai_authenticity_score !== null && (
                          <Badge variant="outline" className="text-[10px] uppercase tracking-[0.18em] border-white/15 gap-1">
                            <Sparkles className="h-2.5 w-2.5 text-[hsl(280_70%_75%)]" />
                            AI {l.ai_quality_score}% quality · {l.ai_authenticity_score}% authentic
                          </Badge>
                        )}
                        {l.ai_status === 'pending' && (
                          <Badge variant="outline" className="text-[10px] uppercase tracking-[0.18em] border-white/15 text-muted-foreground">
                            AI analyzing…
                          </Badge>
                        )}
                        {l.admin_rating_1_5 && (
                          <div className="inline-flex items-center gap-0.5">
                            {[1, 2, 3, 4, 5].map((n) => (
                              <Star
                                key={n}
                                className={`h-3 w-3 ${n <= l.admin_rating_1_5 ? 'text-[hsl(43_92%_56%)] fill-[hsl(43_92%_56%)]' : 'text-white/20'}`}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                      {l.note && (
                        <div className="mt-1.5 text-xs text-[hsl(0_0%_82%)]">{l.note}</div>
                      )}
                      {l.ai_feedback && (
                        <div className="mt-1.5 rounded-md bg-[rgba(140,90,255,0.06)] border border-[rgba(140,90,255,0.25)] px-2.5 py-1.5">
                          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[hsl(280_70%_75%)] font-semibold">
                            <Sparkles className="h-3 w-3" /> AI Feedback
                          </div>
                          <div className="text-[12px] text-[hsl(0_0%_88%)] mt-0.5">{l.ai_feedback}</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Monthly scores */}
          <Card className="bg-white/[0.02] border-white/10">
            <CardContent className="p-4 sm:p-5 space-y-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-[hsl(140_65%_70%)]" />
                <div className="font-display text-sm">Monthly Performance & Salary</div>
              </div>
              {scores.length === 0 ? (
                <div className="text-sm text-muted-foreground text-center p-4 border border-white/10 rounded-md bg-white/[0.02]">
                  No monthly scores computed yet. Admin will run monthly recompute at month-end.
                </div>
              ) : (
                <div className="grid gap-2">
                  {scores.map((s) => (
                    <div
                      key={s.month}
                      className="rounded-md border border-white/10 bg-white/[0.02] p-3"
                      data-testid={`staff-score-row-${s.month}`}
                    >
                      <div className="grid gap-2 sm:grid-cols-[auto,1fr,auto] items-start">
                        <div className="min-w-[60px]">
                          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Month</div>
                          <div className="font-display text-lg">{s.month}</div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 min-w-0">
                          <MiniStat label="Quantity" value={`${s.quantity_score}`} />
                          <MiniStat label="Quality" value={`${s.quality_score}`} />
                          <MiniStat label="Attendance" value={`${s.attendance_score}`} />
                        </div>
                        <div className="text-right min-w-[120px]">
                          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Score</div>
                          <div className={`font-display text-xl tabular-nums ${scoreColor(s.total_score)}`}>
                            {s.total_score}
                          </div>
                          <div className={`text-[11px] font-semibold tabular-nums ${adjustmentColor(s.adjustment_pct)}`}>
                            {s.adjustment_pct > 0 ? '+' : ''}{s.adjustment_pct}%
                          </div>
                          <div className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-[hsl(43_92%_72%)]">
                            <Wallet className="h-3 w-3" /> {fmtBdt(s.final_salary_bdt)}
                          </div>
                        </div>
                      </div>
                      {s.ai_monthly_summary && (
                        <div className="mt-2 rounded-md bg-[rgba(140,90,255,0.06)] border border-[rgba(140,90,255,0.25)] px-2.5 py-2">
                          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[hsl(280_70%_75%)] font-semibold">
                            <Sparkles className="h-3 w-3" /> AI Monthly Review
                          </div>
                          <div className="text-xs text-[hsl(0_0%_88%)] mt-1 whitespace-pre-line">{s.ai_monthly_summary}</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Deposit Claim Dialog */}
      <EditClaimDialog
        claim={editingClaim}
        onClose={() => setEditingClaim(null)}
        onSaved={async () => { setEditingClaim(null); await load(); }}
      />
    </main>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-md border border-white/10 bg-white/[0.02] px-2 py-1.5 text-center">
      <div className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className="font-display text-sm tabular-nums">{value}</div>
    </div>
  );
}

function AttendanceCard({ att, starting, onStart, nowTick }) {
  if (!att) return null;
  const hasStarted = att.has_started;
  const a = att.attendance;
  const hourly = att.hourly_late_rate_bdt;
  // Live projection of "if you start now, how much late deduction"
  const expectedMs = new Date(att.expected_start_utc).getTime();
  const nowMs = nowTick;
  const rawLateMin = Math.max(0, (nowMs - expectedMs) / 60000);
  const graceMin = att.grace_minutes || 5;
  const projectedLateMin = Math.max(0, rawLateMin - graceMin);
  const projectedHours = projectedLateMin / 60;
  const projectedDeduction = Math.round(hourly * projectedHours * 100) / 100;
  const isPastShiftStart = rawLateMin > 0;

  // Post-punch UI
  if (hasStarted && a) {
    const startedAt = new Date(a.actual_start_utc).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    const dateBdt = a.date_bdt;
    return (
      <Card
        className={
          a.on_time
            ? 'border-[rgba(70,200,120,0.55)] bg-[rgba(70,200,120,0.06)]'
            : a.late_deduction_bdt > 0
              ? 'border-[rgba(225,29,46,0.55)] bg-[rgba(225,29,46,0.06)]'
              : 'border-[rgba(245,185,66,0.55)] bg-[rgba(245,185,66,0.05)]'
        }
        data-testid="attendance-card"
      >
        <CardContent className="p-4 sm:p-5 grid gap-3 md:grid-cols-[1fr,auto] items-center">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <PlayCircle className={a.on_time ? 'h-5 w-5 text-[hsl(140_65%_70%)]' : 'h-5 w-5 text-[hsl(0_72%_70%)]'} />
              <div className="font-display text-base">
                Shift started · {startedAt} (BDT)
              </div>
              <Badge variant="outline" className={
                'text-[10px] uppercase tracking-[0.18em] ' +
                (a.on_time
                  ? 'border-[rgba(70,200,120,0.55)] text-[hsl(140_65%_70%)]'
                  : 'border-[rgba(225,29,46,0.55)] text-[hsl(0_72%_70%)]')
              }>
                {a.on_time ? 'ON TIME' : `${Math.round(a.late_minutes)} MIN LATE`}
              </Badge>
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">
              Expected: 10:00 BDT · Actual: {startedAt} · Date: {dateBdt}
            </div>
            {!a.on_time && a.late_deduction_bdt > 0 && (
              <div className="mt-2 text-sm">
                <span className="text-muted-foreground">Late deduction today: </span>
                <span className="text-[hsl(0_72%_70%)] font-bold tabular-nums">
                  −৳{a.late_deduction_bdt.toFixed(2)}
                </span>
                <span className="text-[11px] text-muted-foreground ml-1.5">
                  ({(a.late_hours || 0).toFixed(2)}h × ৳{hourly}/h)
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Pre-punch UI — the big Start button
  return (
    <Card
      className="border-[rgba(245,185,66,0.55)] bg-gradient-to-br from-[rgba(245,185,66,0.08)] to-[rgba(245,185,66,0.02)]"
      data-testid="attendance-card"
    >
      <CardContent className="p-5 sm:p-6 grid gap-4 md:grid-cols-[1fr,auto] items-center">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <Zap className="h-5 w-5 text-[hsl(43_92%_56%)]" />
            <div className="font-display text-lg">Start today&apos;s shift</div>
            <Badge variant="outline" className="text-[10px] uppercase tracking-[0.18em] border-white/15 text-muted-foreground">
              Expected: 10:00 BDT
            </Badge>
          </div>
          {!isPastShiftStart ? (
            <div className="text-xs text-muted-foreground mt-1.5">
              Shift starts at <span className="text-white font-semibold">10:00 AM Bangladesh time</span>.
              Click <span className="text-[hsl(43_92%_72%)]">Start Work</span> when you begin — earlier is fine.
            </div>
          ) : projectedLateMin <= 0 ? (
            <div className="text-xs text-[hsl(140_65%_70%)] mt-1.5 flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5" />
              You&apos;re within the {graceMin}-minute grace window. Click Start Work now to remain on time.
            </div>
          ) : (
            <div className="mt-1.5 text-xs">
              <span className="text-[hsl(0_72%_70%)] font-semibold">
                {Math.round(projectedLateMin)} min late right now
              </span>
              {' '}—{' '}
              starting now will deduct{' '}
              <span className="text-[hsl(0_72%_70%)] font-bold tabular-nums">
                ৳{projectedDeduction.toFixed(2)}
              </span>
              {' '}from today&apos;s salary. Rate: ৳{hourly}/hour.
            </div>
          )}
        </div>
        <Button
          onClick={onStart}
          disabled={starting}
          className="h-14 px-8 bg-gradient-to-r from-[hsl(140_65%_50%)] to-[hsl(155_60%_45%)] hover:from-[hsl(140_65%_58%)] hover:to-[hsl(155_60%_52%)] text-white font-bold text-base border-0 gap-2 shadow-[0_10px_28px_-8px_rgba(46,204,113,0.55)]"
          data-testid="attendance-start-btn"
        >
          {starting ? <Loader2 className="h-5 w-5 animate-spin" /> : <PlayCircle className="h-5 w-5" />}
          Start Work
        </Button>
      </CardContent>
    </Card>
  );
}

function ClaimRow({ claim, onEdit }) {
  const statusMeta = {
    pending_verify: {
      Icon: Clock, cls: 'border-[rgba(245,185,66,0.55)] text-[hsl(43_92%_72%)] bg-[rgba(245,185,66,0.05)]', label: 'PENDING',
    },
    verified: {
      Icon: CheckCircle2, cls: 'border-[rgba(70,200,120,0.55)] text-[hsl(140_65%_70%)] bg-[rgba(70,200,120,0.05)]', label: 'VERIFIED',
    },
    mismatch: {
      Icon: AlertTriangle, cls: 'border-[rgba(225,29,46,0.55)] text-[hsl(0_72%_70%)] bg-[rgba(225,29,46,0.05)]', label: 'MISMATCH',
    },
    rejected: {
      Icon: XCircle, cls: 'border-white/15 text-muted-foreground bg-white/[0.02]', label: 'REJECTED',
    },
  }[claim.status] || {
    Icon: Clock, cls: 'border-white/15 text-muted-foreground', label: (claim.status || '').toUpperCase(),
  };
  const Icon = statusMeta.Icon;
  return (
    <div
      className={'rounded-md border px-2.5 py-2 flex items-center gap-2 flex-wrap ' + statusMeta.cls}
      data-testid={`claim-row-${claim.id}`}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" />
      <div className="text-[10px] uppercase tracking-[0.18em] font-semibold">{statusMeta.label}</div>
      <div className="text-xs font-mono">
        {claim.telegram_id} · <span className="text-white">#{claim.quotex_trader_id}</span>
      </div>
      <div className="text-xs font-mono tabular-nums ml-auto">
        <span className="text-white">${claim.claimed_amount_usd.toFixed(0)}</span>
        {claim.verified_amount_usd != null && (
          <span className="text-muted-foreground"> → ${claim.verified_amount_usd.toFixed(0)}</span>
        )}
      </div>
      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          className="text-[9px] uppercase tracking-[0.14em] px-2 py-0.5 rounded border border-white/15 text-muted-foreground hover:border-white/30 hover:text-white transition-colors inline-flex items-center gap-1"
          data-testid={`claim-edit-${claim.id}`}
          title="Edit amount (for repeated deposits)"
        >
          <Pencil className="h-2.5 w-2.5" /> Edit
        </button>
      )}
      <div className="text-[9px] text-muted-foreground w-full sm:w-auto">
        {(claim.claimed_at || '').slice(11, 16)} · {(claim.claimed_at || '').slice(0, 10)}
        {claim.last_edited_at && (
          <span className="text-[hsl(43_92%_72%)]"> · edited</span>
        )}
      </div>
    </div>
  );
}


// -----------------------------------------------------------------
//                    VIDEO UPLOADER CARD
// -----------------------------------------------------------------

const MANDATORY_SLOTS = [
  { key: '10am', label: '10:00 AM' },
  { key: '3pm',  label: '3:00 PM'  },
  { key: '5pm',  label: '5:00 PM'  },
  { key: '9pm',  label: '9:00 PM'  },
  { key: '10pm', label: '10:00 PM' },
];

function VideoUploaderCard({
  data, slot, setSlot,
  tiktok, setTiktok,
  facebook, setFacebook,
  instagram, setInstagram,
  youtube, setYoutube,
  note, setNote,
  submitting, onSubmit,
}) {
  const today = data?.today;
  const configured = data?.configured_channels || {};
  const items = data?.items || [];
  const target = today?.target_count ?? 18;
  const done = today?.tiktok_count ?? 0;
  const mandatoryDone = today?.mandatory_done ?? 0;
  const mandatoryTarget = today?.mandatory_target ?? 5;
  const flexDone = today?.flex_count ?? 0;
  const flexTarget = today?.flex_target ?? 13;
  const progressPct = Math.min(100, Math.round(((done / target) * 100) || 0));
  const slotState = today?.slots || [];
  const doneMandatoryKeys = new Set(
    slotState.filter((s) => s.mandatory && s.done).map((s) => s.key)
  );

  return (
    <Card className="bg-white/[0.02] border-white/10" data-testid="video-uploader-card">
      <CardContent className="p-4 sm:p-5 space-y-4">
        {/* Header + today progress */}
        <div className="flex items-center gap-2 flex-wrap">
          <Video className="h-4 w-4 text-[hsl(320_70%_78%)]" />
          <div className="font-display text-sm">Video Uploads · Daily Target 18</div>
          <Badge
            variant="outline"
            className="text-[9px] uppercase tracking-[0.18em] border-white/15 text-muted-foreground"
          >
            5 mandatory + 13 flex
          </Badge>
        </div>

        {/* Today progress bar */}
        <div className="rounded-lg border border-[rgba(200,90,180,0.35)] bg-[rgba(200,90,180,0.05)] p-3">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                Today {today?.date_bdt || ''}
              </div>
              <div className="font-display text-2xl text-[hsl(320_70%_82%)] tabular-nums">
                {done}
                <span className="text-sm text-muted-foreground"> / {target} videos</span>
              </div>
            </div>
            <div className="text-right space-y-0.5">
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Mandatory / Flex</div>
              <div className="font-display text-sm">
                <span className={mandatoryDone >= mandatoryTarget ? 'text-[hsl(140_65%_70%)]' : 'text-[hsl(43_92%_72%)]'}>
                  {mandatoryDone}/{mandatoryTarget}
                </span>
                <span className="text-muted-foreground"> · </span>
                <span className={flexDone >= flexTarget ? 'text-[hsl(140_65%_70%)]' : 'text-[hsl(199_84%_70%)]'}>
                  {flexDone}/{flexTarget}
                </span>
              </div>
            </div>
          </div>
          <div className="mt-2 h-2 rounded-full bg-white/8 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[hsl(320_70%_60%)] to-[hsl(280_70%_70%)] transition-[width] duration-300"
              style={{ width: `${progressPct}%` }}
              data-testid="video-progress-bar"
            />
          </div>
          <div className="text-[10px] text-muted-foreground mt-1 tabular-nums">
            {progressPct}% of daily target
          </div>
        </div>

        {/* Mandatory slot chips */}
        <div className="grid grid-cols-5 gap-1.5">
          {MANDATORY_SLOTS.map((s) => {
            const done = doneMandatoryKeys.has(s.key);
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => setSlot(s.key)}
                className={
                  'rounded-md border px-2 py-1.5 text-center transition-colors ' +
                  (slot === s.key
                    ? 'border-[hsl(320_70%_60%)] bg-[rgba(200,90,180,0.15)]'
                    : done
                      ? 'border-[rgba(70,200,120,0.5)] bg-[rgba(70,200,120,0.08)]'
                      : 'border-white/10 bg-white/[0.02] hover:border-white/20')
                }
                data-testid={`slot-btn-${s.key}`}
              >
                <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground">
                  {s.label}
                </div>
                <div className="flex items-center justify-center gap-1 mt-0.5">
                  {done ? (
                    <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(140_65%_70%)]" />
                  ) : (
                    <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                  <span className={done ? 'text-[11px] text-[hsl(140_65%_70%)] font-semibold' : 'text-[11px] text-muted-foreground'}>
                    {done ? 'Done' : 'Pending'}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={() => setSlot('flex')}
          className={
            'w-full rounded-md border px-2 py-1.5 text-center transition-colors ' +
            (slot === 'flex'
              ? 'border-[hsl(199_84%_60%)] bg-[rgba(70,150,220,0.12)]'
              : 'border-white/10 bg-white/[0.02] hover:border-white/20')
          }
          data-testid="slot-btn-flex"
        >
          <div className="flex items-center justify-center gap-1.5">
            <Zap className="h-3.5 w-3.5 text-[hsl(199_84%_68%)]" />
            <span className="text-[11px] uppercase tracking-[0.18em] font-semibold">
              Flex slot · {flexDone}/{flexTarget}
            </span>
          </div>
        </button>

        {/* Configured channels reference */}
        <div className="rounded-md border border-white/8 bg-white/[0.02] p-2.5">
          <div className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground mb-1.5 inline-flex items-center gap-1">
            <Link2 className="h-3 w-3" /> Your allowed channels (URL must contain one)
          </div>
          <div className="grid gap-1 sm:grid-cols-2">
            <ChannelRef icon={<Music2 className="h-3 w-3" />} label="TikTok" refs={configured.tiktok} />
            <ChannelRef icon={<Facebook className="h-3 w-3" />} label="Facebook" refs={configured.facebook} />
            <ChannelRef icon={<Instagram className="h-3 w-3" />} label="Instagram" refs={configured.instagram} />
            <ChannelRef icon={<Youtube className="h-3 w-3" />} label="YouTube" refs={configured.youtube} />
          </div>
          <div className="text-[10px] text-muted-foreground mt-1.5">
            Submissions from any other channel will be rejected. Minimum {today?.min_cross_posts_required ?? 2} cross-posts required.
          </div>
        </div>

        {/* Submit form */}
        <form onSubmit={onSubmit} className="grid gap-3">
          <div className="grid gap-2 sm:grid-cols-[auto,1fr] items-end">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Slot</Label>
              <div className="h-10 px-3 flex items-center rounded-md border border-white/10 bg-white/[0.03] text-sm font-mono tabular-nums min-w-[120px]">
                {slot === 'flex' ? 'Flex' : slot.toUpperCase()}
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-1">
                <Music2 className="h-3 w-3" /> TikTok URL (required)
              </Label>
              <Input
                value={tiktok}
                onChange={(e) => setTiktok(e.target.value)}
                placeholder="https://www.tiktok.com/@yourhandle/video/..."
                className="bg-white/[0.03] border-white/10 h-10 font-mono text-xs"
                data-testid="vu-tiktok"
              />
            </div>
          </div>
          <div className="grid gap-2 sm:grid-cols-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-1">
                <Facebook className="h-3 w-3" /> Facebook URL
              </Label>
              <Input
                value={facebook}
                onChange={(e) => setFacebook(e.target.value)}
                placeholder="facebook.com/..."
                className="bg-white/[0.03] border-white/10 h-9 font-mono text-xs"
                data-testid="vu-facebook"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-1">
                <Instagram className="h-3 w-3" /> Instagram URL
              </Label>
              <Input
                value={instagram}
                onChange={(e) => setInstagram(e.target.value)}
                placeholder="instagram.com/..."
                className="bg-white/[0.03] border-white/10 h-9 font-mono text-xs"
                data-testid="vu-instagram"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-1">
                <Youtube className="h-3 w-3" /> YouTube URL
              </Label>
              <Input
                value={youtube}
                onChange={(e) => setYoutube(e.target.value)}
                placeholder="youtube.com/..."
                className="bg-white/[0.03] border-white/10 h-9 font-mono text-xs"
                data-testid="vu-youtube"
              />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Note (optional)</Label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Trending sound, high-hook opening"
              className="bg-white/[0.03] border-white/10 h-9 text-xs"
              data-testid="vu-note"
            />
          </div>
          <Button
            type="submit" disabled={submitting}
            className="w-full h-11 bg-gradient-to-r from-[hsl(320_70%_55%)] to-[hsl(280_70%_60%)] hover:from-[hsl(320_70%_62%)] hover:to-[hsl(280_70%_66%)] text-white font-bold border-0 gap-2"
            data-testid="vu-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Submit video upload
          </Button>
          <p className="text-[10px] text-muted-foreground text-center">
            Same TikTok URL cannot be submitted twice. Mandatory slots accept 1 video each; extras go to Flex.
          </p>
        </form>

        {/* Recent uploads */}
        {items.length > 0 && (
          <div className="space-y-1.5 pt-2 border-t border-white/8">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              Recent uploads ({items.length})
            </div>
            <div className="grid gap-1.5">
              {items.slice(0, 15).map((v) => (
                <VideoUploadRow key={v.id} v={v} />
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ChannelRef({ icon, label, refs }) {
  const list = refs || [];
  return (
    <div className="flex items-center gap-1.5 min-w-0">
      <span className="text-muted-foreground shrink-0">{icon}</span>
      <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground shrink-0">{label}:</span>
      {list.length === 0 ? (
        <span className="text-[10px] text-[hsl(0_72%_70%)] truncate">Not configured</span>
      ) : (
        <span className="text-[10px] font-mono text-white/85 truncate" title={list.join(', ')}>
          {list.join(', ')}
        </span>
      )}
    </div>
  );
}

function VideoUploadRow({ v }) {
  const okOnTime = v.on_time || v.is_flex;
  const badgeCls = okOnTime
    ? 'border-[rgba(70,200,120,0.5)] text-[hsl(140_65%_70%)]'
    : 'border-[rgba(245,185,66,0.5)] text-[hsl(43_92%_72%)]';
  return (
    <div
      className="rounded-md border border-white/10 bg-white/[0.02] px-2.5 py-2 flex items-center gap-2 flex-wrap"
      data-testid={`video-row-${v.id}`}
    >
      <Badge variant="outline" className={'text-[9px] uppercase tracking-[0.16em] ' + badgeCls}>
        {v.slot_label || v.slot}
      </Badge>
      <a
        href={v.tiktok_url}
        target="_blank" rel="noreferrer noopener"
        className="text-xs font-mono text-white truncate max-w-[220px] hover:underline inline-flex items-center gap-1"
        title={v.tiktok_url}
      >
        <Music2 className="h-3 w-3 text-[hsl(320_70%_78%)]" />
        <span className="truncate">{v.tiktok_url.replace(/^https?:\/\//, '')}</span>
        <ExternalLink className="h-2.5 w-2.5 shrink-0 text-muted-foreground" />
      </a>
      <div className="flex items-center gap-1 ml-auto">
        {(v.cross_post_platforms || []).map((p) => (
          <span key={p} className="text-[9px] uppercase tracking-[0.14em] text-[hsl(199_84%_70%)] font-semibold">
            {p.slice(0, 2)}
          </span>
        ))}
        <span className="text-[9px] text-muted-foreground tabular-nums">
          · {v.cross_post_count}/3
        </span>
      </div>
      <div className="text-[9px] text-muted-foreground w-full sm:w-auto">
        {(v.actual_utc || '').slice(11, 16)} · {v.date_bdt}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
//                MARKET ANALYST / SIGNAL PROVIDER CARD
// -----------------------------------------------------------------

function MarketAnalystCard({
  signalData, commissionData,
  status, setStatus,
  trader, setTrader,
  telegram, setTelegram,
  inactiveReason, setInactiveReason,
  liveConverted, setLiveConverted,
  note, setNote,
  submitting, onSubmit,
  onApprove, onToggleLive,
  approvingId,
}) {
  const today = signalData?.today;
  const items = signalData?.items || [];
  const target = today?.target_count ?? 15;
  const delivered = today?.delivered_count ?? 0;
  const inactive = today?.inactive_count ?? 0;
  const liveConversions = today?.live_conversions ?? 0;
  const progressPct = Math.min(100, Math.round((delivered / target) * 100));
  const commissions = commissionData?.items || [];
  const monthly = commissionData?.monthly_summary_usd || {};
  const pendingCount = commissionData?.pending_approval_for_me || 0;
  const todayBdt = today?.date_bdt || '';
  const todaysItems = items.filter((it) => it.date_bdt === todayBdt);

  return (
    <Card className="bg-white/[0.02] border-white/10" data-testid="market-analyst-card">
      <CardContent className="p-4 sm:p-5 space-y-4">
        {/* Header */}
        <div className="flex items-center gap-2 flex-wrap">
          <Radio className="h-4 w-4 text-[hsl(280_70%_75%)]" />
          <div className="font-display text-sm">Signal Provider · Daily Target 15</div>
          <Badge variant="outline" className="text-[9px] uppercase tracking-[0.18em] border-white/15 text-muted-foreground">
            Telegram call + Quotex attribution
          </Badge>
          {pendingCount > 0 && (
            <Badge variant="outline" className="text-[10px] uppercase tracking-[0.16em] border-[rgba(245,185,66,0.55)] bg-[rgba(245,185,66,0.1)] text-[hsl(43_92%_72%)] gap-1">
              <AlertTriangle className="h-3 w-3" /> {pendingCount} pending approval
            </Badge>
          )}
        </div>

        {/* Today progress + monthly commission */}
        <div className="grid gap-3 sm:grid-cols-[2fr,1fr]">
          <div className="rounded-lg border border-[rgba(140,90,255,0.35)] bg-[rgba(140,90,255,0.05)] p-3">
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                  Today {todayBdt}
                </div>
                <div className="font-display text-2xl text-[hsl(280_70%_82%)] tabular-nums">
                  {delivered}
                  <span className="text-sm text-muted-foreground"> / {target} delivered</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Inactive / Live conv.</div>
                <div className="font-display text-sm">
                  <span className="text-[hsl(43_92%_72%)] tabular-nums">{inactive}</span>
                  <span className="text-muted-foreground"> · </span>
                  <span className="text-[hsl(140_65%_70%)] tabular-nums">{liveConversions}</span>
                </div>
              </div>
            </div>
            <div className="mt-2 h-2 rounded-full bg-white/8 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[hsl(280_70%_60%)] to-[hsl(320_70%_65%)] transition-[width] duration-300"
                style={{ width: `${progressPct}%` }}
                data-testid="signal-progress-bar"
              />
            </div>
            <div className="text-[10px] text-muted-foreground mt-1 tabular-nums">
              {progressPct}% of daily target
            </div>
          </div>
          <div className="rounded-lg border border-[hsl(140_65%_55%)]/40 bg-[rgba(70,200,120,0.05)] p-3">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground inline-flex items-center gap-1">
              <HandCoins className="h-3 w-3" /> This month commission
            </div>
            <div className="mt-1 font-display text-2xl text-[hsl(140_65%_70%)] tabular-nums">
              ${(monthly.as_signal_provider_approved || 0).toFixed(2)}
              <span className="text-xs text-muted-foreground"> USD approved</span>
            </div>
            {(monthly.as_signal_provider_pending || 0) > 0 && (
              <div className="text-[11px] text-[hsl(43_92%_72%)] tabular-nums">
                + ${monthly.as_signal_provider_pending.toFixed(2)} pending
              </div>
            )}
            {(monthly.as_message_staff_approved || monthly.as_message_staff_pending) ? (
              <div className="text-[10px] text-muted-foreground mt-1">
                As message staff: ${(monthly.as_message_staff_approved || 0).toFixed(2)} approved
                {monthly.as_message_staff_pending > 0 ? ` · $${monthly.as_message_staff_pending.toFixed(2)} pending` : ''}
              </div>
            ) : null}
          </div>
        </div>

        {/* Submission form */}
        <form onSubmit={onSubmit} className="grid gap-3">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setStatus('delivered')}
              className={
                'flex-1 rounded-md border px-3 py-2 transition-colors ' +
                (status === 'delivered'
                  ? 'border-[hsl(140_65%_50%)] bg-[rgba(70,200,120,0.12)]'
                  : 'border-white/10 bg-white/[0.02] hover:border-white/20')
              }
              data-testid="sig-status-delivered"
            >
              <div className="flex items-center justify-center gap-1.5">
                <UserCheck className="h-3.5 w-3.5 text-[hsl(140_65%_70%)]" />
                <span className="text-[11px] uppercase tracking-[0.18em] font-semibold">Delivered</span>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setStatus('inactive')}
              className={
                'flex-1 rounded-md border px-3 py-2 transition-colors ' +
                (status === 'inactive'
                  ? 'border-[hsl(43_92%_56%)] bg-[rgba(245,185,66,0.12)]'
                  : 'border-white/10 bg-white/[0.02] hover:border-white/20')
              }
              data-testid="sig-status-inactive"
            >
              <div className="flex items-center justify-center gap-1.5">
                <Ban className="h-3.5 w-3.5 text-[hsl(43_92%_72%)]" />
                <span className="text-[11px] uppercase tracking-[0.18em] font-semibold">Inactive User</span>
              </div>
            </button>
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-1">
                <MessageCircle className="h-3 w-3" /> Telegram username
              </Label>
              <Input
                value={telegram}
                onChange={(e) => setTelegram(e.target.value)}
                placeholder="@username or +8801..."
                className="bg-white/[0.03] border-white/10 h-10"
                data-testid="sig-telegram"
              />
            </div>
            {status === 'delivered' ? (
              <div>
                <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-1">
                  <Hash className="h-3 w-3" /> Quotex Trader ID
                </Label>
                <Input
                  value={trader}
                  onChange={(e) => setTrader(e.target.value)}
                  placeholder="e.g. 12345678"
                  className="bg-white/[0.03] border-white/10 h-10 font-mono"
                  data-testid="sig-trader"
                />
              </div>
            ) : (
              <div>
                <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> Reason (why inactive)
                </Label>
                <Input
                  value={inactiveReason}
                  onChange={(e) => setInactiveReason(e.target.value)}
                  placeholder="e.g. User did not respond to 3 calls"
                  className="bg-white/[0.03] border-white/10 h-10"
                  data-testid="sig-inactive-reason"
                />
              </div>
            )}
          </div>

          {status === 'delivered' && (
            <label className="flex items-center gap-2 cursor-pointer rounded-md border border-[rgba(70,200,120,0.3)] bg-[rgba(70,200,120,0.05)] px-3 py-2">
              <input
                type="checkbox"
                checked={liveConverted}
                onChange={(e) => setLiveConverted(e.target.checked)}
                className="h-4 w-4 accent-[hsl(140_65%_50%)]"
                data-testid="sig-live-converted"
              />
              <PhoneCall className="h-3.5 w-3.5 text-[hsl(140_65%_70%)]" />
              <span className="text-xs">
                <span className="font-semibold">Deposit converted LIVE in call</span>
                <span className="text-muted-foreground"> — 1% commission (pending your approval after Quotex postback confirms deposit)</span>
              </span>
            </label>
          )}

          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Note (optional)</Label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Recommended GBP/USD, small lot size"
              className="bg-white/[0.03] border-white/10 h-9 text-xs"
              data-testid="sig-note"
            />
          </div>
          <Button
            type="submit" disabled={submitting}
            className="w-full h-11 bg-gradient-to-r from-[hsl(280_70%_55%)] to-[hsl(320_70%_60%)] hover:from-[hsl(280_70%_62%)] hover:to-[hsl(320_70%_66%)] text-white font-bold border-0 gap-2"
            data-testid="sig-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {status === 'delivered' ? 'Log delivered signal' : 'Log inactive user'}
          </Button>
        </form>

        {/* Pending approvals */}
        {pendingCount > 0 && (
          <div className="rounded-md border border-[rgba(245,185,66,0.35)] bg-[rgba(245,185,66,0.05)] p-3 space-y-2">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(43_92%_72%)] font-semibold flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5" /> Pending Approvals ({pendingCount})
            </div>
            <div className="text-[10px] text-muted-foreground">
              Quotex postback verified these deposits. Review + approve. Live-call = you get 1%. Split (linked by message staff) = 0.5%/0.5%.
            </div>
            {commissions.filter((c) => c.status === 'pending_approval').map((c) => (
              <div
                key={c.id}
                className="rounded-md border border-white/10 bg-white/[0.02] px-2.5 py-2 flex items-center gap-2 flex-wrap"
                data-testid={`commission-pending-${c.id}`}
              >
                <div className="text-xs font-mono">
                  <span className="text-white">#{c.trader_id}</span>
                  <span className="text-muted-foreground"> · {c.telegram_username}</span>
                </div>
                <div className="text-xs">
                  Deposit <span className="font-mono text-white">${c.deposit_amount_usd.toFixed(0)}</span>
                  <span className="text-muted-foreground"> → you get </span>
                  <span className="font-mono text-[hsl(140_65%_70%)]">${c.signal_provider_commission_usd.toFixed(2)}</span>
                </div>
                {c.attribution_type === 'live_call' ? (
                  <Badge variant="outline" className="text-[9px] uppercase tracking-[0.14em] border-[rgba(70,200,120,0.55)] text-[hsl(140_65%_70%)]">
                    Live call · 1%
                  </Badge>
                ) : (
                  <div className="text-[10px] text-muted-foreground">
                    Split · {c.message_staff_name || 'msg staff'}
                  </div>
                )}
                <div className="ml-auto flex items-center gap-1">
                  <Button
                    size="sm"
                    disabled={approvingId === c.id}
                    onClick={() => onApprove(c.id, true)}
                    className="h-7 gap-1 bg-[hsl(140_65%_45%)] hover:bg-[hsl(140_65%_52%)] text-white text-[10px] uppercase tracking-[0.14em]"
                    data-testid={`commission-approve-${c.id}`}
                  >
                    <ThumbsUp className="h-3 w-3" /> Approve
                  </Button>
                  <Button
                    size="sm"
                    disabled={approvingId === c.id}
                    onClick={() => onApprove(c.id, false)}
                    variant="outline"
                    className="h-7 gap-1 border-[rgba(225,29,46,0.5)] text-[hsl(0_72%_70%)] text-[10px] uppercase tracking-[0.14em]"
                    data-testid={`commission-reject-${c.id}`}
                  >
                    <ThumbsDown className="h-3 w-3" /> Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Today's signal list */}
        {todaysItems.length > 0 && (
          <div className="space-y-1.5 pt-2 border-t border-white/8">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              Today&apos;s signals ({todaysItems.length})
            </div>
            <div className="grid gap-1.5">
              {todaysItems.slice(0, 20).map((d) => (
                <SignalDeliveryRow key={d.id} d={d} onToggleLive={onToggleLive} />
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SignalDeliveryRow({ d, onToggleLive }) {
  const isDelivered = d.status === 'delivered';
  const cls = isDelivered
    ? 'border-[rgba(70,200,120,0.35)] bg-[rgba(70,200,120,0.05)]'
    : 'border-[rgba(245,185,66,0.35)] bg-[rgba(245,185,66,0.05)]';
  return (
    <div
      className={'rounded-md border px-2.5 py-2 flex items-center gap-2 flex-wrap ' + cls}
      data-testid={`signal-row-${d.id}`}
    >
      {isDelivered ? (
        <UserCheck className="h-3.5 w-3.5 text-[hsl(140_65%_70%)]" />
      ) : (
        <Ban className="h-3.5 w-3.5 text-[hsl(43_92%_72%)]" />
      )}
      <div className="text-[10px] uppercase tracking-[0.18em] font-semibold">
        {isDelivered ? 'DELIVERED' : 'INACTIVE'}
      </div>
      {isDelivered ? (
        <div className="text-xs font-mono">
          <span className="text-white">#{d.trader_id}</span>
          <span className="text-muted-foreground"> · {d.telegram_username}</span>
        </div>
      ) : (
        <div className="text-xs">
          <span className="text-white">{d.telegram_username}</span>
          <span className="text-muted-foreground"> — {d.inactive_reason}</span>
        </div>
      )}
      {isDelivered && (
        <button
          type="button"
          onClick={() => onToggleLive(d.id, d.live_call_deposit_converted)}
          className={
            'ml-auto text-[9px] uppercase tracking-[0.14em] px-2 py-0.5 rounded border transition-colors ' +
            (d.live_call_deposit_converted
              ? 'border-[hsl(140_65%_50%)] text-[hsl(140_65%_70%)] bg-[rgba(70,200,120,0.1)]'
              : 'border-white/15 text-muted-foreground hover:border-white/30')
          }
          data-testid={`signal-live-toggle-${d.id}`}
        >
          <span className="inline-flex items-center gap-1">
            <PhoneCall className="h-2.5 w-2.5" />
            {d.live_call_deposit_converted ? 'Live converted' : 'Mark live'}
          </span>
        </button>
      )}
      <div className="text-[9px] text-muted-foreground w-full sm:w-auto sm:ml-auto">
        {(d.created_at || '').slice(11, 16)}
      </div>
    </div>
  );
}


// -----------------------------------------------------------------
//                    AI SUPERVISOR CARD (all roles)
// -----------------------------------------------------------------

function AiSupervisorCard({ reminderData, waNumber, setWaNumber, saving, onSaveWhatsapp, onAck, ackingId }) {
  const today = reminderData?.today;
  const items = reminderData?.items || [];
  const todaysItems = items.filter((it) => it.date_bdt === today?.date_bdt);
  const pending = todaysItems.filter((r) => !r.ack_at);
  const brief = today?.brief;

  return (
    <Card className="bg-gradient-to-br from-[rgba(140,90,255,0.06)] to-[rgba(70,150,220,0.04)] border border-[rgba(140,90,255,0.25)]" data-testid="ai-supervisor-card">
      <CardContent className="p-4 sm:p-5 space-y-4">
        {/* Header */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[hsl(280_70%_55%)] to-[hsl(199_84%_55%)] flex items-center justify-center">
            <Bot className="h-4 w-4 text-white" />
          </div>
          <div>
            <div className="font-display text-sm">AlveeFx Ops Assistant</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              Claude-powered · WhatsApp reminders every 10 min
            </div>
          </div>
          {pending.length > 0 && (
            <Badge variant="outline" className="ml-auto text-[10px] uppercase tracking-[0.16em] border-[rgba(245,185,66,0.55)] bg-[rgba(245,185,66,0.1)] text-[hsl(43_92%_72%)] gap-1">
              <Bell className="h-3 w-3" /> {pending.length} pending
            </Badge>
          )}
        </div>

        {/* WhatsApp number input */}
        <div className="grid gap-2 sm:grid-cols-[1fr,auto] items-end">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-1">
              <PhoneCall className="h-3 w-3" /> Your WhatsApp number
            </Label>
            <Input
              value={waNumber}
              onChange={(e) => setWaNumber(e.target.value)}
              placeholder="+8801XXXXXXXXX"
              className="bg-white/[0.03] border-white/10 h-10 font-mono text-xs"
              data-testid="wa-number-input"
            />
            <div className="text-[10px] text-muted-foreground mt-1">
              Assistant tumake ei number e reminder pathabe. Number save na kore hole reminder off.
            </div>
          </div>
          <Button
            type="button"
            onClick={onSaveWhatsapp}
            disabled={saving}
            className="h-10 gap-2 bg-[hsl(140_65%_45%)] hover:bg-[hsl(140_65%_52%)] text-white text-xs"
            data-testid="wa-save-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save
          </Button>
        </div>

        {/* Today's brief from admin */}
        {brief ? (
          <div className="rounded-md border border-white/10 bg-white/[0.02] p-3">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-1.5 mb-1">
              <FileText className="h-3 w-3 text-[hsl(199_84%_68%)]" /> Today&apos;s Brief from Admin
            </div>
            <div className="text-xs leading-relaxed whitespace-pre-wrap text-white/90">
              {brief.brief_text}
            </div>
          </div>
        ) : (
          <div className="rounded-md border border-dashed border-white/10 bg-white/[0.02] p-3 text-[11px] text-muted-foreground text-center">
            No brief from admin for today yet. Reminders will be generic until admin sets one.
          </div>
        )}

        {/* Today's reminders */}
        {todaysItems.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-baseline justify-between">
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                Today&apos;s Reminders
              </div>
              <div className="text-[10px] text-muted-foreground tabular-nums">
                {today?.ack_count ?? 0} done · {today?.pending_count ?? 0} pending · {today?.total_sent ?? 0} total
              </div>
            </div>
            <div className="grid gap-1.5">
              {todaysItems.map((r) => (
                <ReminderRow key={r.id} r={r} onAck={onAck} disabled={ackingId === r.id} />
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ReminderRow({ r, onAck, disabled }) {
  const acked = !!r.ack_at;
  const actionLabel = r.ack_action === 'done' ? 'DONE' : r.ack_action === 'skip' ? 'SKIP' : r.ack_action === 'later' ? 'LATER' : '';
  const actionCls = r.ack_action === 'done'
    ? 'border-[hsl(140_65%_45%)] text-[hsl(140_65%_70%)]'
    : r.ack_action === 'skip'
      ? 'border-[hsl(0_72%_55%)] text-[hsl(0_72%_70%)]'
      : 'border-[hsl(43_92%_56%)] text-[hsl(43_92%_72%)]';
  return (
    <div
      className={
        'rounded-md border px-3 py-2 space-y-1.5 ' +
        (acked ? 'border-white/8 bg-white/[0.02] opacity-70' : 'border-[rgba(140,90,255,0.35)] bg-[rgba(140,90,255,0.05)]')
      }
      data-testid={`reminder-row-${r.id}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <Timer className="h-3 w-3 text-muted-foreground shrink-0" />
        <span className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground tabular-nums">
          {(r.sent_at || '').slice(11, 16)} · {r.task_summary}
        </span>
        {r.provider === 'mocked' && (
          <Badge variant="outline" className="text-[9px] uppercase tracking-[0.14em] border-[rgba(245,185,66,0.5)] text-[hsl(43_92%_72%)]">
            MOCKED
          </Badge>
        )}
        {acked && (
          <Badge variant="outline" className={'text-[9px] uppercase tracking-[0.14em] ml-auto ' + actionCls}>
            {actionLabel}
          </Badge>
        )}
      </div>
      <div className="text-xs leading-relaxed whitespace-pre-wrap text-white/90">
        {r.message_text}
      </div>
      {!acked && (
        <div className="flex items-center gap-1 pt-1">
          <Button
            size="sm"
            disabled={disabled}
            onClick={() => onAck(r.id, 'done')}
            className="h-7 gap-1 bg-[hsl(140_65%_45%)] hover:bg-[hsl(140_65%_52%)] text-white text-[10px] uppercase tracking-[0.14em]"
            data-testid={`reminder-done-${r.id}`}
          >
            <CheckSquare className="h-3 w-3" /> Mark done
          </Button>
          <Button
            size="sm"
            disabled={disabled}
            onClick={() => onAck(r.id, 'later')}
            variant="outline"
            className="h-7 gap-1 border-white/15 text-[10px] uppercase tracking-[0.14em]"
            data-testid={`reminder-later-${r.id}`}
          >
            <Clock className="h-3 w-3" /> Later
          </Button>
          <Button
            size="sm"
            disabled={disabled}
            onClick={() => onAck(r.id, 'skip')}
            variant="outline"
            className="h-7 gap-1 border-[rgba(225,29,46,0.4)] text-[hsl(0_72%_70%)] text-[10px] uppercase tracking-[0.14em]"
            data-testid={`reminder-skip-${r.id}`}
          >
            <XCircle className="h-3 w-3" /> Skip
          </Button>
        </div>
      )}
      {acked && r.ack_note && (
        <div className="text-[10px] text-muted-foreground italic">Note: {r.ack_note}</div>
      )}
    </div>
  );
}


// -----------------------------------------------------------------
//               EDIT DEPOSIT CLAIM DIALOG
// -----------------------------------------------------------------

function EditClaimDialog({ claim, onClose, onSaved }) {
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (claim) {
      setAmount(String(claim.claimed_amount_usd || ''));
      setNote(claim.note || '');
    }
  }, [claim]);

  if (!claim) return null;

  async function submit(e) {
    e.preventDefault();
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error('Enter a valid positive amount');
      return;
    }
    setSaving(true);
    try {
      const r = await staffEditDepositClaim(claim.id, {
        claimed_amount_usd: amt,
        note: note.trim(),
      });
      if (r.re_verified) {
        toast.success(`Claim updated & auto-verified against Quotex postback! ($${r.verified_amount_usd})`);
      } else if (r.status === 'pending_verify') {
        toast.success('Claim updated. Waiting for matching Quotex postback…');
      } else {
        toast.success(`Claim updated — status: ${r.status}`);
      }
      await onSaved();
    } catch (e2) {
      toast.error(apiErrMsg(e2, 'Update failed'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={!!claim} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md bg-[hsl(240_10%_6%)] border-white/10" data-testid="edit-claim-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Pencil className="h-4 w-4 text-[hsl(43_92%_72%)]" /> Edit deposit amount
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Trader <span className="text-white font-mono">#{claim.quotex_trader_id}</span> ({claim.telegram_id}).
            Same trader multiple times deposit korle new cumulative amount likhe save koro —
            system Quotex postback e latest sumdep er sathe re-verify korbe.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                Current claimed
              </Label>
              <div className="h-10 rounded-md border border-white/10 bg-white/[0.03] px-3 flex items-center text-sm font-mono tabular-nums text-muted-foreground">
                ${claim.claimed_amount_usd?.toFixed(2)}
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                Verified so far
              </Label>
              <div className="h-10 rounded-md border border-white/10 bg-white/[0.03] px-3 flex items-center text-sm font-mono tabular-nums text-[hsl(140_65%_70%)]">
                {claim.verified_amount_usd != null ? `$${claim.verified_amount_usd.toFixed(2)}` : '—'}
              </div>
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              New claimed amount (USD)
            </Label>
            <Input
              type="number" step="0.01" min="0" required
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="bg-white/[0.03] border-white/10 h-10 font-mono tabular-nums"
              data-testid="edit-claim-amount"
              autoFocus
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              Note (optional)
            </Label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. User deposited 2nd time — cumulative $250"
              className="bg-white/[0.03] border-white/10 h-9 text-xs"
              data-testid="edit-claim-note"
            />
          </div>
          <div className="flex gap-2 pt-1">
            <Button
              type="submit" disabled={saving}
              className="flex-1 bg-[hsl(140_65%_45%)] hover:bg-[hsl(140_65%_52%)] text-white gap-1"
              data-testid="edit-claim-save"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save & re-verify
            </Button>
            <Button
              type="button" variant="outline"
              onClick={onClose}
              className="border-white/10"
              data-testid="edit-claim-cancel"
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}



// =========================================================================
// PersonalCallQueueCard — shows approved / called / completed personal-call
// requests. Signal Provider (market_analyst) can Claim + Complete calls.
// Telegram Support gets read-only visibility.
// =========================================================================
function PersonalCallQueueCard({
  pcallData, filter, setFilter, onClaim, onComplete, actionId, currentStaffId, onRefresh,
}) {
  const items = pcallData?.items || [];
  const approvedOpen = pcallData?.approved_open || 0;
  const calledOpen = pcallData?.called_open || 0;
  const minDep = pcallData?.min_deposit_usd || 500;

  const filtered = items.filter((r) => {
    if (filter === 'all') return true;
    if (filter === 'open') return r.status === 'approved' || r.status === 'called';
    return r.status === filter;
  });

  const totalApproved = items.filter((r) => r.status === 'approved').length;
  const totalCalled = items.filter((r) => r.status === 'called').length;
  const totalCompleted = items.filter((r) => r.status === 'completed').length;
  const totalPending = items.filter((r) => r.status === 'pending_verify').length;

  return (
    <Card className="bg-white/[0.02] border-white/10" data-testid="personal-call-queue-card">
      <CardContent className="p-4 sm:p-5 space-y-4">
        {/* Header */}
        <div className="flex items-center gap-2 flex-wrap">
          <PhoneCall className="h-4 w-4 text-[hsl(43_92%_60%)]" />
          <div className="font-display text-sm">Personal Call Queue</div>
          <Badge variant="outline" className="text-[9px] uppercase tracking-[0.18em] border-white/15 text-muted-foreground">
            ${minDep}+ deposit · Quotex verified
          </Badge>
          {approvedOpen > 0 && (
            <Badge variant="outline" className="text-[10px] uppercase tracking-[0.16em] border-[rgba(70,200,120,0.55)] bg-[rgba(70,200,120,0.08)] text-[hsl(140_65%_72%)] gap-1">
              <CheckCircle2 className="h-3 w-3" /> {approvedOpen} unclaimed
            </Badge>
          )}
          {calledOpen > 0 && (
            <Badge variant="outline" className="text-[10px] uppercase tracking-[0.16em] border-[rgba(199,180,80,0.55)] bg-[rgba(255,220,80,0.06)] text-[hsl(43_92%_75%)] gap-1">
              <PhoneCall className="h-3 w-3" /> {calledOpen} in progress
            </Badge>
          )}
          <div className="ml-auto">
            <Button size="sm" variant="outline" onClick={onRefresh} className="h-7 gap-1.5 border-white/10 bg-white/[0.02]" data-testid="pcall-queue-refresh">
              <RefreshCw className="h-3 w-3" /> Refresh
            </Button>
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-1.5">
          {[
            { key: 'open', label: 'Open queue', count: totalApproved + totalCalled },
            { key: 'approved', label: 'Approved', count: totalApproved },
            { key: 'called', label: 'Called', count: totalCalled },
            { key: 'completed', label: 'Completed', count: totalCompleted },
            { key: 'pending_verify', label: 'Awaiting deposit', count: totalPending },
            { key: 'all', label: 'All', count: items.length },
          ].map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setFilter(t.key)}
              className={
                'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.18em] transition-colors ' +
                (filter === t.key
                  ? 'border-[hsl(43_92%_56%)] bg-[rgba(245,185,66,0.12)] text-[hsl(43_92%_72%)]'
                  : 'border-white/10 bg-white/[0.02] text-muted-foreground hover:text-white')
              }
              data-testid={`pcall-filter-${t.key}`}
            >
              <span>{t.label}</span>
              <span className="rounded-full bg-white/[0.08] px-1.5 font-mono tabular-nums text-[9px]">
                {t.count}
              </span>
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="text-sm text-muted-foreground text-center p-6 border border-white/10 rounded-md bg-white/[0.02]">
            {filter === 'open'
              ? 'No open personal-call requests in the queue right now. New verified requests will appear here automatically.'
              : 'No matching requests.'}
          </div>
        ) : (
          <div className="grid gap-2">
            {filtered.map((r) => (
              <PersonalCallRow
                key={r.id}
                r={r}
                onClaim={onClaim}
                onComplete={onComplete}
                actionId={actionId}
                currentStaffId={currentStaffId}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PersonalCallRow({ r, onClaim, onComplete, actionId, currentStaffId }) {
  const status = r.status;
  const isMine = r.called_by_staff_id === currentStaffId;
  const busy = actionId === r.id;

  const borderCls =
    status === 'approved'
      ? 'border-[rgba(70,200,120,0.4)] bg-[rgba(70,200,120,0.05)]'
      : status === 'called'
      ? 'border-[rgba(199,180,80,0.45)] bg-[rgba(255,220,80,0.05)]'
      : status === 'completed'
      ? 'border-[rgba(140,90,255,0.4)] bg-[rgba(140,90,255,0.05)]'
      : status === 'pending_verify'
      ? 'border-white/10 bg-white/[0.02]'
      : 'border-[rgba(225,29,46,0.35)] bg-[rgba(225,29,46,0.04)]';

  return (
    <div
      className={`rounded-md border p-3 ${borderCls}`}
      data-testid={`pcall-row-${r.id}`}
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              <Hash className="h-3 w-3" />
              <span className="font-mono text-white">{r.quotex_trader_id}</span>
            </div>
            <div className="inline-flex items-center gap-1.5 text-xs">
              <MessageCircle className="h-3 w-3 text-[hsl(199_84%_68%)]" />
              <span className="text-white">{r.telegram_username}</span>
            </div>
            {r.verified_deposit_usd ? (
              <Badge variant="outline" className="text-[9px] uppercase tracking-[0.14em] border-[rgba(70,200,120,0.5)] text-[hsl(140_65%_70%)] gap-1">
                <DollarSign className="h-2.5 w-2.5" />
                {`$${Number(r.verified_deposit_usd).toFixed(0)} verified`}
              </Badge>
            ) : null}
            {status === 'approved' && (
              <Badge variant="outline" className="text-[9px] uppercase tracking-[0.14em] border-[rgba(70,200,120,0.55)] text-[hsl(140_65%_72%)]">
                Ready to call
              </Badge>
            )}
            {status === 'called' && (
              <Badge variant="outline" className="text-[9px] uppercase tracking-[0.14em] border-[rgba(199,180,80,0.6)] text-[hsl(43_92%_75%)] gap-1">
                <PhoneCall className="h-2.5 w-2.5" />
                {isMine ? 'You are calling' : `${r.called_by_staff_name} on it`}
              </Badge>
            )}
            {status === 'completed' && (
              <Badge variant="outline" className="text-[9px] uppercase tracking-[0.14em] border-[rgba(140,90,255,0.55)] text-[hsl(280_70%_80%)] gap-1">
                <CheckCircle2 className="h-2.5 w-2.5" />
                Done by {r.completed_by_staff_name || 'staff'}
              </Badge>
            )}
            {status === 'pending_verify' && (
              <Badge variant="outline" className="text-[9px] uppercase tracking-[0.14em] border-white/15 text-muted-foreground gap-1">
                <Clock className="h-2.5 w-2.5" />
                Awaiting Quotex ≥ deposit
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3 flex-wrap text-[10px] text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-2.5 w-2.5" /> Submitted {fmtDate(r.submitted_at)}
            </span>
            {r.verified_at && (
              <span className="inline-flex items-center gap-1">
                <ShieldCheck className="h-2.5 w-2.5 text-[hsl(140_65%_65%)]" />
                Verified {fmtDate(r.verified_at)}
              </span>
            )}
            {r.called_at && (
              <span className="inline-flex items-center gap-1">
                <PhoneCall className="h-2.5 w-2.5 text-[hsl(43_92%_65%)]" />
                Claimed {fmtDate(r.called_at)}
              </span>
            )}
          </div>
          {r.note && (
            <div className="text-xs text-[hsl(0_0%_85%)] mt-1 leading-snug">
              <span className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground mr-1">User note:</span>
              {r.note}
            </div>
          )}
          {r.staff_note && (
            <div className="text-xs text-[hsl(0_0%_85%)] mt-1 leading-snug">
              <span className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground mr-1">Staff note:</span>
              {r.staff_note}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1.5 shrink-0">
          {status === 'approved' && (
            <>
              <a
                href={buildTelegramDeepLink(r.telegram_username)}
                target="_blank"
                rel="noreferrer"
                data-testid={`pcall-open-telegram-${r.id}`}
              >
                <Button size="sm" variant="outline" className="h-7 gap-1 border-white/10 bg-white/[0.02] text-[10px] uppercase tracking-[0.14em]">
                  <Send className="h-3 w-3" /> Open TG
                </Button>
              </a>
              <Button
                size="sm"
                disabled={busy}
                onClick={() => onClaim(r.id)}
                className="h-7 gap-1 bg-[hsl(43_92%_56%)] hover:bg-[hsl(43_92%_62%)] text-black text-[10px] uppercase tracking-[0.14em] font-bold"
                data-testid={`pcall-claim-${r.id}`}
              >
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <PhoneCall className="h-3 w-3" />}
                Claim
              </Button>
            </>
          )}
          {status === 'called' && isMine && (
            <>
              <a
                href={buildTelegramDeepLink(r.telegram_username)}
                target="_blank"
                rel="noreferrer"
                data-testid={`pcall-open-telegram-${r.id}`}
              >
                <Button size="sm" variant="outline" className="h-7 gap-1 border-white/10 bg-white/[0.02] text-[10px] uppercase tracking-[0.14em]">
                  <Send className="h-3 w-3" /> Open TG
                </Button>
              </a>
              <Button
                size="sm"
                disabled={busy}
                onClick={() => onComplete(r.id)}
                className="h-7 gap-1 bg-[hsl(140_65%_45%)] hover:bg-[hsl(140_65%_52%)] text-white text-[10px] uppercase tracking-[0.14em] font-bold"
                data-testid={`pcall-complete-${r.id}`}
              >
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                Complete
              </Button>
            </>
          )}
          {status === 'called' && !isMine && (
            <Badge variant="outline" className="text-[9px] uppercase tracking-[0.14em] border-white/15 text-muted-foreground">
              Locked
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
}

// Build a best-effort Telegram deep link. Accepts @handle, plain username, or +phone.
function buildTelegramDeepLink(input) {
  const s = String(input || '').trim();
  if (!s) return 'https://t.me/';
  if (s.startsWith('+')) {
    const digits = s.replace(/[^0-9]/g, '');
    if (digits) return `https://t.me/+${digits}`;
  }
  const handle = s.replace(/^@/, '').replace(/[^A-Za-z0-9_]/g, '');
  if (handle) return `https://t.me/${handle}`;
  return 'https://t.me/';
}
